import { useState } from 'react'
import { useFinance } from '../context/FinanceContext'
import { useSettings } from '../context/SettingsContext'
import { getThemeColors } from '../lib/constants'
import { trackCalculatorUse } from '../lib/analytics'
import PageShell from '../components/layout/PageShell'
import EmergencyFund from '../components/scenarios/EmergencyFund'
import SavingsGoal from '../components/scenarios/SavingsGoal'
import NetWorthTracker from '../components/scenarios/NetWorthTracker'
import CostOfLiving from '../components/scenarios/CostOfLiving'

const TABS = [
  { key: 'emergency', label: 'Emergency Fund', icon: ShieldIcon },
  { key: 'savings', label: 'Savings Goal', icon: TargetIcon },
  { key: 'networth', label: 'Net Worth', icon: ChartIcon },
  { key: 'col', label: 'Cost of Living', icon: MapIcon },
]

export default function FinancialHealth() {
  const { state } = useFinance()
  const { trackCalculatorOpen } = useSettings()
  const colors = getThemeColors(state.theme)
  const validTabs = TABS.map(t => t.key)
  const [activeTab, setActiveTab] = useState(validTabs.includes(state.activeTab) ? state.activeTab : 'emergency')

  return (
    <PageShell
      title="Financial Health"
      subtitle="Emergency funds, savings goals, net worth tracking, and cost of living comparisons"
    >
      <div className="flex gap-1 p-1 rounded-2xl glass mb-6 max-w-fit">
        {TABS.map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            onClick={() => { setActiveTab(key); trackCalculatorUse(key); trackCalculatorOpen(key) }}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 ${
              activeTab === key
                ? 'text-heading shadow-sm'
                : 'text-muted hover:text-heading'
            }`}
            style={activeTab === key ? {
              background: `linear-gradient(135deg, ${colors.primary}22, ${colors.secondary}22)`,
              border: `1px solid ${colors.primary}33`,
            } : {}}
          >
            <Icon active={activeTab === key} />
            <span className="hidden sm:inline">{label}</span>
          </button>
        ))}
      </div>

      <div className="animate-fade-in">
        {activeTab === 'emergency' && <EmergencyFund />}
        {activeTab === 'savings' && <SavingsGoal />}
        {activeTab === 'networth' && <NetWorthTracker />}
        {activeTab === 'col' && <CostOfLiving />}
      </div>
    </PageShell>
  )
}

function ShieldIcon({ active }) {
  return (
    <svg className="w-4 h-4" style={active ? { color: 'var(--primary)' } : {}} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m0-10.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.75c0 5.592 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.57-.598-3.75h-.152c-3.196 0-6.1-1.249-8.25-3.286z" />
    </svg>
  )
}

function TargetIcon({ active }) {
  return (
    <svg className="w-4 h-4" style={active ? { color: 'var(--primary)' } : {}} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v12m-3-2.818l.879.659c1.171.879 3.07.879 4.242 0 1.172-.879 1.172-2.303 0-3.182C13.536 12.219 12.768 12 12 12c-.725 0-1.45-.22-2.003-.659-1.106-.879-1.106-2.303 0-3.182s2.9-.879 4.006 0l.415.33M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  )
}

function ChartIcon({ active }) {
  return (
    <svg className="w-4 h-4" style={active ? { color: 'var(--primary)' } : {}} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z" />
    </svg>
  )
}

function MapIcon({ active }) {
  return (
    <svg className="w-4 h-4" style={active ? { color: 'var(--primary)' } : {}} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 6.75V15m6-6v8.25m.503 3.498l4.875-2.437c.381-.19.622-.58.622-1.006V4.82c0-.836-.88-1.38-1.628-1.006l-3.869 1.934c-.317.159-.69.159-1.006 0L9.503 3.252a1.125 1.125 0 00-1.006 0L3.622 5.689C3.24 5.88 3 6.27 3 6.695V19.18c0 .836.88 1.38 1.628 1.006l3.869-1.934c.317-.159.69-.159 1.006 0l4.994 2.497c.317.158.69.158 1.006 0z" />
    </svg>
  )
}
